/**
 * Canonical work-order (ärende) page spec.
 *
 * This file is THE spec for the unified work-order flow
 * (docs/plans/2026-10-07-unified-work-order-flow.md §1, §2, §5). Every surface
 * renders the same sections in the same order with the same words:
 *
 *   - web admin:  src/Pages/Förvaltning/components/WorkOrderDetailModal.tsx
 *   - web guest:  src/Pages/Guest/GuestTaskPage.tsx
 *   - iOS:        Husappen/.../WorkorderDetailView.swift
 *
 * Admin-only controls sit in-place on their sections; iOS and the guest page
 * simply don't render them. `workOrderSections.test.tsx` fails when a page
 * renders the sections in another order.
 */

export const WORK_ORDER_SECTIONS = [
    'header',         // 1  ÄRENDE · prioritet · status · titel · Försenad
    'building',       // 2  Byggnad
    'description',    // 3  Beskrivning
    'details',        // 4  Detaljer
    'components',     // 5  Komponenter + Hitta komponenterna
    'issuePhotos',    // 6  Foton från felanmälan
    'attachments',    // 7  Bilagor
    'documentation',  // 8  Dokumentation
    'reportedIssues', // 9  Fel hittade under arbetet (Felanmäl nytt fel, while open)
    'completion',     // 10 Slutför ärende / Slutförande
    // No "Krav för slutförande" section: the requirements show where they apply,
    // as the asterisks and the efterbilder hint in the completion form.
] as const;

export type WorkOrderSectionId = typeof WORK_ORDER_SECTIONS[number];

/** `data-testid` carried by every top-level section wrapper. */
export function workOrderSectionTestId(id: WorkOrderSectionId): string {
    return `wo-section-${id}`;
}

/** Glossary (spec §5), verbatim on all surfaces. */
export const WORK_ORDER_LABELS = {
    // Entity
    entity: 'Ärende',
    entityBadge: 'ÄRENDE',
    entityInProject: 'Delmoment',

    // Statuses
    statusOpen: 'Öppen',
    statusCompleted: 'Slutförd',
    statusCancelled: 'Arkiverad utan åtgärd',
    overdue: 'Försenad',

    // Section titles
    sectionBuilding: 'Byggnad',
    sectionDescription: 'Beskrivning',
    sectionDetails: 'Detaljer',
    sectionComponents: 'Komponenter',
    sectionFindComponents: 'Hitta komponenterna',
    sectionIssuePhotos: 'Foton från felanmälan',
    sectionAttachments: 'Bilagor',
    sectionDocumentation: 'Dokumentation',
    sectionReportedIssues: 'Fel hittade under arbetet',
    reportIssue: 'Felanmäl nytt fel',
    reportedIssuesEmpty: 'Hittar du ett fel medan du arbetar? Felanmäl det här.',
    reportedIssueActive: 'Ny',
    reportedIssueConverted: 'Omvandlad till ärende',
    reportedIssueArchived: 'Arkiverad',
    reportedIssueNoComponent: 'Annat',
    fromWorkOrder: 'Från ärende',
    sectionCompletion: 'Slutför ärende',
    sectionCompletionSummary: 'Slutförande',

    // Documentation groups
    notes: 'Anteckningar',
    checklists: 'Checklistor',
    beforePictures: 'Före-bilder',
    progressPictures: 'Pågående-bilder',
    afterPictures: 'Efter-bilder',
    editDocumentation: 'Ändra dokumentation',

    // Details rows
    created: 'Skapad',
    createdBy: 'av',
    dueDate: 'Förfallodatum',
    project: 'Projekt',
    unit: 'Lägenhet/lokal',
    assigned: 'Tilldelad',
    unassigned: 'Otilldelad',
    external: 'Extern',
    externalCompletedReadOnly: 'Ärendet är slutfört. Externa mottagare ser det skrivskyddat i sin portal i 30 dagar.',
    externalLinksCancelled: 'Ärendet är arkiverat utan åtgärd. Externa mottagare ser det inte längre i sin portal.',
    externalEmailNotSent: 'E-postmeddelandet kunde inte skickas – kopiera länken och skicka den själv.',
    noDescription: 'Ingen beskrivning',
    edit: 'Ändra',
    add: 'Lägg till',
    directions: 'Vägbeskrivning',

    // Requirements

    // Assignment
    assignInternal: 'Tilldela intern',
    delegateExternal: 'Delegera externt',
    revoke: 'Återkalla',
    copyLink: 'Kopiera länk',
    assignModalTitle: 'Tilldela ärende',

    // Components
    addComponent: 'Lägg till',
    removeComponent: 'Ta bort komponent',
    show: 'Visa',
    locationOnPhoto: 'Plats på foto',
    roofPlan: 'Takplan',
    map: 'Karta',
    openInMaps: 'Öppna i kartor',

    // Completion form (spec §2)
    complete: 'Slutför ärende',
    completeConfirmTitle: 'Slutför ärendet?',
    completeConfirmBody: 'När du slutför skickas ärendet in till förvaltaren och kan inte ändras efteråt. Är allt klart?',
    completeConfirmCancel: 'Avbryt',
    completeConfirmOk: 'Ja, slutför',
    comment: 'Kommentar',
    commentPlaceholder: 'Beskriv utfört arbete...',
    condition: 'Skick',
    conditionNotSet: 'Ej angivet',
    conditionScale: '0 = behöver ersättas / 10 = mycket bra skick',
    conditionYearsLeft: (years: number) => `Ca ${years} år kvar`,
    conditionComment: 'Skickskommentar (valfritt)',
    actualCost: 'Faktisk kostnad',
    actualCostOptional: 'Faktisk kostnad (valfritt)',
    investmentShare: 'Investeringsandel',
    investmentEquals: (kr: string) => `= ${kr} kr investering`,
    completedAt: 'Slutförd',

    // Completion validation (mirrored by the server)
    validationPhotoRequired: 'Lägg till minst en efterbild',
    validationCommentRequired: 'Kommentar krävs',
    validationConditionsRequired: 'Ange skick för alla länkade komponenter',
    validationConditionRange: 'Skickvärde måste vara mellan 0 och 10',
    validationNotOpen: 'Ärendet är redan avslutat',

    // Autosave
    autosaveIdle: 'Sparas automatiskt',
    autosaveSaving: 'Sparar…',
    autosaveSaved: 'Sparat',

    // Admin actions
    duplicate: 'Duplicera',
    cancelAction: 'Arkivera utan åtgärd',
    delete: 'Ta bort',
    reopen: 'Återutfärda',
} as const;
